import { useState, useEffect } from 'react';
import { galleryAPI } from '@/lib/api';

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await galleryAPI.getAll();
        // Ensure all image URLs are absolute
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const imagesWithAbsoluteUrls = response.data.map(img => ({
          ...img,
          image_url: img.image_url.startsWith('http') ? img.image_url : `${backendUrl}${img.image_url}`
        }));
        setImages(imagesWithAbsoluteUrls);
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#D4AF37]">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl sm:text-6xl font-light text-white mb-4">
            گالری <span className="text-[#D4AF37]">سمینارها</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            تصاویر سمینارها، رویدادها و لحظات به‌یادماندنی آریا مهاجر آسیا
          </p>
        </div>

        {/* Gallery Grid */}
        {images.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {images.map((image) => (
              <div
                key={image.id}
                onClick={() => setSelectedImage(image)}
                className="group relative overflow-hidden rounded-xl aspect-square cursor-pointer"
              >
                <img
                  src={image.image_url}
                  alt={image.title_fa}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <div>
                    <h3 className="text-white font-semibold text-lg mb-1">{image.title_fa}</h3>
                    {image.description_fa && (
                      <p className="text-zinc-300 text-sm line-clamp-2">{image.description_fa}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-zinc-400 py-20">
            <p className="text-xl">هنوز تصویری در گالری قرار نگرفته است.</p>
          </div>
        )}

        {/* Lightbox Modal */}
        {selectedImage && (
          <div
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          >
            <div className="max-w-5xl w-full">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title_fa}
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
              <div className="text-center mt-6">
                <h3 className="text-2xl font-semibold text-white mb-2">
                  {selectedImage.title_fa}
                </h3>
                {selectedImage.description_fa && (
                  <p className="text-zinc-400">{selectedImage.description_fa}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;
